/**
 * FAIR by Design - Validation Rule Browser
 *
 * Interactive browser for exploring all 25+ validation rules with filtering,
 * search, and inline examples.
 */

import React, { useState, useEffect } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

/**
 * Validation rule interface (matches server-side)
 */
interface ValidationRule {
  id: string;
  name: string;
  fairPrinciple: string;
  severity: 'critical' | 'warning' | 'info';
  category: string;
  description: string;
  rationale: string;
  examples: {
    bad: string;
    good: string;
    explanation: string;
  }[];
  fixGuidance: string;
  estimatedEffort: '5min' | '30min' | '2hr' | '1day';
  tags?: string[];
}

interface RuleStatistics {
  totalRules: number;
  bySeverity: {
    critical: number;
    warning: number;
    info: number;
  };
  byCategory: Record<string, number>;
  byPrinciple: Record<string, number>;
}

export const RuleBrowser: React.FC = () => {
  const { get } = useApiClient();

  // State
  const [rules, setRules] = useState<ValidationRule[]>([]);
  const [filteredRules, setFilteredRules] = useState<ValidationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<RuleStatistics | null>(null);
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPrinciple, setSelectedPrinciple] = useState<string>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Available filter options
  const [availablePrinciples, setAvailablePrinciples] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);

  /**
   * Fetch all rules and statistics on mount (cached via useApiClient)
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch rules (critical - throws on failure)
        const rulesData = await get<{ rules: ValidationRule[] }>('/api/fair-by-design/rules');
        setRules(rulesData.rules || []);
        setFilteredRules(rulesData.rules || []);

        // Fetch statistics (optional)
        try {
          const statsData = await get<{ statistics: RuleStatistics }>('/api/fair-by-design/stats');
          setStats(statsData.statistics);
        } catch { /* optional endpoint */ }

        // Fetch filter options (optional)
        try {
          const principlesData = await get<{ principles: string[] }>('/api/fair-by-design/principles');
          setAvailablePrinciples(principlesData.principles || []);
        } catch { /* optional endpoint */ }

        try {
          const categoriesData = await get<{ categories: string[] }>('/api/fair-by-design/categories');
          setAvailableCategories(categoriesData.categories || []);
        } catch { /* optional endpoint */ }
      } catch (err) {
        console.error('Error fetching rules:', err);
        setError(err instanceof Error ? err.message : 'Failed to load rules');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [get]);

  /**
   * Apply filters whenever search term or filter selections change
   */
  useEffect(() => {
    let filtered = [...rules];

    // Search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        rule =>
          rule.name.toLowerCase().includes(lowerSearch) ||
          rule.description.toLowerCase().includes(lowerSearch) ||
          rule.rationale.toLowerCase().includes(lowerSearch) ||
          rule.tags?.some(tag => tag.toLowerCase().includes(lowerSearch))
      );
    }

    // Principle filter
    if (selectedPrinciple !== 'all') {
      filtered = filtered.filter(rule => rule.fairPrinciple === selectedPrinciple);
    }

    // Severity filter
    if (selectedSeverity !== 'all') {
      filtered = filtered.filter(rule => rule.severity === selectedSeverity);
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(rule => rule.category === selectedCategory);
    }

    setFilteredRules(filtered);
  }, [searchTerm, selectedPrinciple, selectedSeverity, selectedCategory, rules]);

  /**
   * Toggle rule expansion
   */
  const toggleRule = (ruleId: string) => {
    setExpandedRuleId(expandedRuleId === ruleId ? null : ruleId);
  };

  /**
   * Get severity badge class
   */
  const getSeverityClass = (severity: string): string => {
    switch (severity) {
      case 'critical':
        return 'severity-critical';
      case 'warning':
        return 'severity-warning';
      case 'info':
        return 'severity-info';
      default:
        return '';
    }
  };

  /**
   * Get FAIR principle color
   */
  const getPrincipleClass = (principle: string): string => {
    const firstLetter = principle.charAt(0);
    switch (firstLetter) {
      case 'F':
        return 'principle-findable';
      case 'A':
        return 'principle-accessible';
      case 'I':
        return 'principle-interoperable';
      case 'R':
        return 'principle-reusable';
      default:
        return '';
    }
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div className="rule-browser-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading validation rules...</p>
        </div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <div className="rule-browser-container">
        <div className="error-state">
          <h3>Error Loading Rules</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  /**
   * Main render
   */
  return (
    <div className="rule-browser-container">
      {/* Header with statistics */}
      <div className="rule-browser-header">
        <h1>FAIR by Design Validation Rules</h1>
        <p className="subtitle">
          Comprehensive catalog of {stats?.totalRules || rules.length} rules for assessing database schemas against FAIR principles
        </p>

        {stats && (
          <div className="stats-bar">
            <div className="stat-item">
              <span className="stat-label">Total Rules</span>
              <span className="stat-value">{stats.totalRules}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Critical</span>
              <span className="stat-value severity-critical">{stats.bySeverity.critical}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Warnings</span>
              <span className="stat-value severity-warning">{stats.bySeverity.warning}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Info</span>
              <span className="stat-value severity-info">{stats.bySeverity.info}</span>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="rule-browser-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="search">Search</label>
            <input
              type="text"
              id="search"
              placeholder="Search rules, descriptions, or tags..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="principle">FAIR Principle</label>
            <select
              id="principle"
              value={selectedPrinciple}
              onChange={e => setSelectedPrinciple(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Principles</option>
              {availablePrinciples.map(principle => (
                <option key={principle} value={principle}>
                  {principle}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="severity">Severity</label>
            <select
              id="severity"
              value={selectedSeverity}
              onChange={e => setSelectedSeverity(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Categories</option>
              {availableCategories.map(category => (
                <option key={category} value={category}>
                  {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-results">
          <p>
            Showing <strong>{filteredRules.length}</strong> of <strong>{rules.length}</strong> rules
          </p>
          {(searchTerm || selectedPrinciple !== 'all' || selectedSeverity !== 'all' || selectedCategory !== 'all') && (
            <button
              className="clear-filters-btn"
              onClick={() => {
                setSearchTerm('');
                setSelectedPrinciple('all');
                setSelectedSeverity('all');
                setSelectedCategory('all');
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Rules list */}
      <div className="rules-list">
        {filteredRules.length === 0 ? (
          <div className="no-results">
            <p>No rules match your filters. Try adjusting your search criteria.</p>
          </div>
        ) : (
          filteredRules.map(rule => (
            <div key={rule.id} className={`rule-card ${expandedRuleId === rule.id ? 'expanded' : ''}`}>
              {/* Rule header - clickable to expand */}
              <div className="rule-header" onClick={() => toggleRule(rule.id)}>
                <div className="rule-header-left">
                  <span className={`rule-id ${getPrincipleClass(rule.fairPrinciple)}`}>{rule.id}</span>
                  <h3 className="rule-name">{rule.name}</h3>
                </div>
                <div className="rule-header-right">
                  <span className={`principle-badge ${getPrincipleClass(rule.fairPrinciple)}`}>
                    {rule.fairPrinciple}
                  </span>
                  <span className={`severity-badge ${getSeverityClass(rule.severity)}`}>{rule.severity}</span>
                  <span className="effort-badge">{rule.estimatedEffort}</span>
                  <span className="expand-icon">{expandedRuleId === rule.id ? '▼' : '▶'}</span>
                </div>
              </div>

              {/* Rule description - always visible */}
              <div className="rule-summary">
                <p>{rule.description}</p>
              </div>

              {/* Expanded content */}
              {expandedRuleId === rule.id && (
                <div className="rule-details">
                  {/* Rationale */}
                  <div className="detail-section">
                    <h4>Why This Matters</h4>
                    <p>{rule.rationale}</p>
                  </div>

                  {/* Examples */}
                  <div className="detail-section">
                    <h4>Examples</h4>
                    {rule.examples.map((example, idx) => (
                      <div key={idx} className="example-pair">
                        <div className="example-bad">
                          <h5>❌ Bad Example</h5>
                          <pre>
                            <code>{example.bad}</code>
                          </pre>
                        </div>
                        <div className="example-good">
                          <h5>✅ Good Example</h5>
                          <pre>
                            <code>{example.good}</code>
                          </pre>
                        </div>
                        <div className="example-explanation">
                          <p>
                            <strong>Explanation:</strong> {example.explanation}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Fix guidance */}
                  <div className="detail-section">
                    <h4>How to Fix</h4>
                    <p className="fix-guidance">{rule.fixGuidance}</p>
                  </div>

                  {/* Tags */}
                  {rule.tags && rule.tags.length > 0 && (
                    <div className="detail-section">
                      <h4>Tags</h4>
                      <div className="tag-list">
                        {rule.tags.map(tag => (
                          <span key={tag} className="tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RuleBrowser;
